import { useState } from 'react';
import Lightbox from 'yet-another-react-lightbox';
import 'yet-another-react-lightbox/styles.css';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Badge } from '@/components/ui/badge';
import { Play } from 'lucide-react';

interface PhotoGalleryProps {
  images: string[];
  videoUrl?: string;
  title: string;
  euroStandard?: string;
  critAir?: string;
  isVerified?: boolean;
}

export const PhotoGallery = ({ 
  images, 
  videoUrl, 
  title, 
  euroStandard, 
  critAir,
  isVerified 
}: PhotoGalleryProps) => {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const lightboxSlides = images.map(img => ({ src: img }));

  return (
    <div className="space-y-4">
      {/* Image principale */}
      <div className="relative aspect-video w-full overflow-hidden rounded-lg bg-muted">
        {images[0] ? (
          <>
            <button 
              onClick={() => openLightbox(0)}
              className="w-full h-full"
            >
              <OptimizedImage
                src={images[0]}
                alt={`${title} - Photo principale`}
                className="h-full w-full object-cover cursor-pointer hover:scale-105 transition-transform duration-300"
              />
            </button>
            
            {/* Badges superposés */}
            <div className="absolute top-4 left-4 flex flex-wrap gap-2">
              {isVerified && (
                <Badge className="bg-green-600 hover:bg-green-700 text-white font-semibold">
                  🚛 Véhicule vérifié
                </Badge>
              )}
              {euroStandard && (
                <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
                  {euroStandard}
                </Badge>
              )}
              {critAir && (
                <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm">
                  Crit'Air {critAir === 'exempt' ? 'Exempté' : critAir}
                </Badge>
              )}
            </div>

            {/* Indicateur vidéo */}
            {videoUrl && (
              <div className="absolute top-4 right-4">
                <Badge className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-1">
                  <Play className="h-3 w-3" />
                  Vidéo disponible
                </Badge>
              </div>
            )}
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-muted-foreground">
            Aucune image disponible
          </div>
        )}
      </div>

      {/* Miniatures */}
      {images.length > 1 && (
        <div className="grid grid-cols-4 gap-2 sm:grid-cols-6 md:grid-cols-8">
          {images.slice(1, 9).map((image, index) => (
            <button
              key={index}
              onClick={() => openLightbox(index + 1)}
              className="aspect-square overflow-hidden rounded-md bg-muted hover:opacity-75 transition-opacity"
            >
              <OptimizedImage
                src={image}
                alt={`${title} - Photo ${index + 2}`}
                className="h-full w-full object-cover"
              />
            </button>
          ))}
          
          {/* Vidéo en miniature */}
          {videoUrl && (
            <a
              href={videoUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="aspect-square overflow-hidden rounded-md bg-blue-100 hover:bg-blue-200 transition-colors flex items-center justify-center"
            >
              <Play className="h-8 w-8 text-blue-600" />
            </a>
          )}
        </div>
      )}

      {/* Lightbox pour navigation plein écran */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        slides={lightboxSlides}
        index={lightboxIndex}
      />
    </div>
  );
};
